import React, { useEffect, useRef, useState } from 'react'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js'
import {
    getDatasetAtEvent,
    getElementAtEvent,
    getElementsAtEvent,
    Line,
} from 'react-chartjs-2'
import { format, subMonths } from 'date-fns'

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
)

const AnalysisLineChart = ({ dataChart, showChart }) => {
    const chartRef = useRef()
    const [filteredExpenses, setFilteredExpenses] = useState([])
    const [filteredRevenue, setFilteredRevenue] = useState([])
    const [filteredBalance, setFilteredBalance] = useState([])
    const [filteredDates, setFilteredDates] = useState([])
    const [chartTitle, setChartTitle] = useState("")

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top',
                align: 'center',
                padding: {
                    top: 170,
                },
            },
            title: {
                display: true,
                text: `${chartTitle}`,
                align: 'start',
                padding: {
                    bottom: 20,
                    top: 40,
                },
            },
        },
    }

    const data = {
        labels: filteredDates,
        datasets: [
            {
                label: 'Total Exp',
                data: filteredExpenses.length ? filteredExpenses : [],
                borderColor: 'rgb(255, 0, 0)',
                backgroundColor: 'rgba(255, 0, 0, 0.5)',
            },
            {
                label: 'Total Revenue',
                data: filteredRevenue.length ? filteredRevenue : [],
                borderColor: 'rgb(0, 255, 0)',
                backgroundColor: 'rgba(0, 255, 0, 0.5)',
            },
            {
                label: 'Payout Balance',
                data: filteredBalance.length ? filteredBalance : [],
                borderColor: 'rgb(0, 0, 255)',
                backgroundColor: 'rgba(0, 0, 255, 0.5)',
            }
        ],
    }

    const onClickElement = (event) => {
        let elementClicked = getElementAtEvent(chartRef.current, event)

        printElementAtEvent(getDatasetAtEvent(chartRef.current, event))
    }

    const printElementAtEvent = (element) => {
        if (!element.length) return

        const { datasetIndex, index } = element[0]
    }

    useEffect(() => {
        if (dataChart && dataChart.length) {
            let dateTo = new Date(dataChart[0].reportingMonth)
            let dateFrom = new Date(dataChart[dataChart.length - 1].reportingMonth)
            setChartTitle(`${format(dateFrom, 'MMM yyyy')} - ${format(dateTo, 'MMM yyyy')}`) 

            const reverseDataChart = [...dataChart].reverse()
            const newData = {}

            for (let i = 0; i < reverseDataChart.length; i++) {
                const currentData = reverseDataChart[i]
                const currentDate = new Date(currentData.reportingMonth)
                const currentMonth = currentDate.getMonth()
                const currentYear = currentDate.getFullYear()
                const key = currentYear + '-' + currentMonth

                if (!newData[key]) {
                    newData[key] = {
                        reportingMonth: currentData.reportingMonth,
                        totalExpenses: currentData.totalExpense,
                        totalRevenues: currentData.totalRevenue,
                        totalBalances: currentData.payoutBalance
                    }
                } else {
                    newData[key] = {
                        reportingMonth: currentData.reportingMonth,
                        totalExpenses: newData[key].totalExpenses + currentData.totalExpense,
                        totalRevenues: newData[key].totalRevenues + currentData.totalRevenue,
                        totalBalances: newData[key].totalBalances + currentData.payoutBalance
                    }
                }

                if (i < reverseDataChart.length - 1) {
                    const nextData = reverseDataChart[i + 1]
                    const nextDate = new Date(nextData.reportingMonth)
                    const nextMonth = nextDate.getMonth()
                    const nextYear = nextDate.getFullYear()

                    const monthsDiff = (nextYear - currentYear) * 12 + (nextMonth - currentMonth);

                    for (let j = 1; j < monthsDiff; j++) {
                        const newMonth = new Date(currentYear, currentMonth + j, 1)
                        const newKey = newMonth.getFullYear() + '-' + newMonth.getMonth()

                        newData[newKey] = {
                            reportingMonth: newMonth.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
                            totalExpenses: newData[key].totalExpenses,
                            totalRevenues: newData[key].totalRevenues,
                            totalBalances: newData[key].totalBalances
                        }
                    }
                }
            }

            const finalData = Object.values(newData).map(item => ({
                reportingMonth: item.reportingMonth,
                totalExpenses: item.totalExpenses,
                totalRevenues: item.totalRevenues,
                totalBalances: item.totalBalances
            }))

            setFilteredExpenses(
                finalData
                    .map((report) =>
                        report.totalExpenses
                    )
            )
            setFilteredRevenue(
                finalData
                    .map((report) =>
                        report.totalRevenues
                    )
            )
            setFilteredBalance(
                finalData
                    .map((report) =>
                        report.totalBalances
                    )
            )
            setFilteredDates(
                finalData
                    .map((report) => {
                        let date = new Date(report.reportingMonth)
                        return format(date, 'MMM yyyy')
                    })
            )
        }
    }, [dataChart])

    const content = () => {
        if (showChart && dataChart && dataChart.length >= 1) {
            return (
                <Line
                    ref={chartRef}
                    options={options}
                    data={data}
                    onClick={onClickElement}
                    height={300}
                />
            )
        } else if (showChart && (!dataChart || dataChart.length === 0)) {
            return (
                <p className="text-center p-4">
                    No statements match your search criteria.
                </p>
            )
        }
    }

    return <>{content()}</>
}

export default AnalysisLineChart
